import React, { useCallback, useState } from 'react';
import ContinuousScanHero from '../components/ContinuousScanHero';
import PdaPage from '../components/PdaPage';
import PdaScanCapture from '../components/PdaScanCapture';
import QrmarkCameraScanner from '../components/QrmarkCameraScanner';
import type { QrmarkCameraPayload } from '../components/QrmarkCameraScanner';
import {
  DeliveryLabelAttachmentReceipt,
  DeliveryLabelTask,
  DeliveryLabelTrace,
  getDeliveryLabelTask,
  listGeneratedDeliveryNotes,
  scanDeliveryLabelTask,
} from '../services/api';
import {
  DELIVERY_NOTE_NO_RE,
  DELIVERY_TASK_RE,
  extractDeliveryBusinessCode,
  FORMAL_TRACE_RE,
} from '../utils/pdaCodes';
import {
  findGeneratedDeliveryNoteByScanCode,
  getGeneratedDeliveryNoteBindId,
} from '../utils/deliveryPalletBind';
import { getPdaErrorMessage } from '../utils/pdaErrors';
import { notifyError, notifyInfo, notifySuccess } from '../utils/pdaFeedback';
import { navigateTo } from '../utils/navigation';

type LabelVerificationStage = 'waiting_task' | 'loading_task' | 'attaching' | 'completed' | 'error';
type LabelScanFeedback = { text: string; tone: 'info' | 'success' | 'error' };

function nextTaskStage(task: DeliveryLabelTask): LabelVerificationStage {
  return task.remaining_label_count === 0 ? 'completed' : 'attaching';
}

function LabelVerification() {
  const [stage, setStage] = useState<LabelVerificationStage>('waiting_task');
  const [task, setTask] = useState<DeliveryLabelTask | null>(null);
  const [lastTrace, setLastTrace] = useState<DeliveryLabelTrace | null>(null);
  const [displayScanCode, setDisplayScanCode] = useState('');
  const [processing, setProcessing] = useState(false);
  const [cameraScannerOpen, setCameraScannerOpen] = useState(false);
  const [scanFeedback, setScanFeedback] = useState<LabelScanFeedback>({
    text: '等待扫描预送货任务码',
    tone: 'info',
  });
  const scannerEnabled = !processing && !cameraScannerOpen;

  const resetTask = useCallback(() => {
    setTask(null);
    setLastTrace(null);
    setDisplayScanCode('');
    setStage('waiting_task');
    setScanFeedback({ text: '等待扫描预送货任务码', tone: 'info' });
  }, []);

  const handleScan = useCallback(async (rawText: string) => {
    const code = extractDeliveryBusinessCode(rawText, task ? 'formal' : 'tagged');
    if (!code) {
      const message = '未识别到任务码或正式标签，请重新扫描';
      setScanFeedback({ text: message, tone: 'error' });
      notifyError(message);
      return;
    }
    setDisplayScanCode(code);
    if (!task && !DELIVERY_TASK_RE.test(code)) {
      const message = '请先扫描预送货任务码';
      setScanFeedback({ text: `已接收 ${code} · ${message}`, tone: 'error' });
      notifyError(message);
      return;
    }
    if (task && !DELIVERY_TASK_RE.test(code) && !FORMAL_TRACE_RE.test(code)) {
      const message = '请扫描预送货任务码或 8 位正式标签';
      setScanFeedback({ text: `已接收 ${code} · 格式不正确`, tone: 'error' });
      notifyError(message);
      return;
    }
    if (task?.remaining_label_count === 0 && !DELIVERY_TASK_RE.test(code)) {
      const message = '当前任务已完成，请扫描下一张预送货任务码';
      setScanFeedback({ text: message, tone: 'info' });
      notifyInfo(message);
      return;
    }

    const isTaskScan = DELIVERY_TASK_RE.test(code);
    const previousTask = task;
    setScanFeedback({
      text: isTaskScan ? `已接收任务码 ${code} · 正在加载` : `已接收标签 ${code} · 正在确认`,
      tone: 'info',
    });
    setProcessing(true);
    setStage(isTaskScan ? 'loading_task' : 'attaching');
    try {
      let nextTask: DeliveryLabelTask;
      let nextTrace: DeliveryLabelTrace | null = null;
      let attachment: DeliveryLabelAttachmentReceipt | null = null;
      let responseMessage = '';

      if (DELIVERY_NOTE_NO_RE.test(code)) {
        const lookupParams = {
          delivery_note_no: code,
          keyword: code,
          note_no: code,
          page: 1,
          page_size: 20,
        };
        const noteResponse = await listGeneratedDeliveryNotes(lookupParams);
        const note = findGeneratedDeliveryNoteByScanCode(noteResponse.data, code);
        const noteId = getGeneratedDeliveryNoteBindId(note);
        if (!note || !noteId) {
          throw new Error(`未找到预送货任务 ${code}`);
        }
        const taskResponse = await getDeliveryLabelTask(noteId);
        nextTask = taskResponse.data;
      } else {
        const payload = isTaskScan
          ? {
            code,
            confirm_attachment: false as const,
            enter_pmi: false as const,
          }
          : {
            code,
            task_code: previousTask!.task_code,
            confirm_attachment: true as const,
            enter_pmi: false as const,
          };
        const response = await scanDeliveryLabelTask(payload);
        nextTask = response.data.task;
        nextTrace = response.data.trace || null;
        attachment = response.data.attach || null;
        responseMessage = response.msg || '';
      }

      if (!isTaskScan && previousTask?.task_code && nextTask.task_code !== previousTask.task_code) {
        const message = '正式标签不属于当前预送货任务';
        setScanFeedback({ text: `${code} · ${message}`, tone: 'error' });
        notifyError(message);
        setStage(nextTaskStage(previousTask));
        return;
      }

      setTask(nextTask);
      setLastTrace(nextTrace);
      setStage(nextTaskStage(nextTask));

      if (isTaskScan) {
        const taskName = nextTask.note?.note_no || nextTask.task_code;
        setScanFeedback({ text: `任务已加载 · ${taskName}`, tone: 'success' });
        notifySuccess(`已加载任务：${taskName}`);
        return;
      }
      if (attachment?.idempotent || attachment?.first_attachment === false) {
        const message = responseMessage || '该标签已确认贴标';
        setScanFeedback({ text: `${code} · ${message}`, tone: 'info' });
        notifyInfo(message);
      } else {
        const attachedCode = attachment?.trace_code || code;
        setScanFeedback({ text: `标签 ${attachedCode} 已确认`, tone: 'success' });
        notifySuccess(`标签 ${attachedCode} 已确认`);
      }
    } catch (error) {
      const message = getPdaErrorMessage(error, isTaskScan ? '任务加载失败' : '贴标确认失败');
      setScanFeedback({ text: `${code} · ${message}`, tone: 'error' });
      notifyError(message);
      setStage(previousTask ? nextTaskStage(previousTask) : 'error');
    } finally {
      setProcessing(false);
    }
  }, [task]);

  const handleCameraScan = useCallback(async (payload: QrmarkCameraPayload) => {
    await handleScan(JSON.stringify(payload));
  }, [handleScan]);

  const expected = task?.expected_label_count || 0;
  const printed = task?.printed_label_count || 0;
  const attached = task?.attached_label_count || 0;
  const remaining = task?.remaining_label_count || 0;
  const printStatus = expected > 0 && printed >= expected ? '已打印' : '未打印';
  const isCompleted = stage === 'completed';
  const waitingForTask = !task;

  return (
    <PdaPage
      footer={isCompleted ? (
        <button className="pmi-primary-btn label-flow-footer-btn" onClick={resetTask} type="button">
          扫描下一张任务
        </button>
      ) : undefined}
      onBack={() => navigateTo('/home')}
      pageClassName={`continuous-pmi-page label-verification-page continuous-pmi-page--${isCompleted ? 'pass' : 'scanning'}${cameraScannerOpen ? ' continuous-pmi-page--camera-open' : ''}`}
      title="贴标核验"
    >
      <PdaScanCapture
        ariaLabel="贴标核验扫码输入"
        enabled={scannerEnabled}
        focusKey={`${stage}-${task?.task_code || ''}`}
        onEmptyScan={() => {
          setScanFeedback({ text: '扫码程序未返回数据', tone: 'error' });
          notifyError('扫码程序未返回数据');
        }}
        onScan={handleScan}
        testId="label-verification-scan-form"
      />

      <ContinuousScanHero
        cameraOpen={cameraScannerOpen}
        code={displayScanCode || lastTrace?.trace_code}
        focusReady={!processing}
        identified={isCompleted}
        pillText={waitingForTask ? '预送货任务码' : '80×80 正式标签'}
        scannerAction={(
          <QrmarkCameraScanner
            disabled={processing}
            embedded
            mode="plain"
            onOpenChange={setCameraScannerOpen}
            onScan={handleCameraScan}
            plainCodeKind={waitingForTask ? 'task' : 'formal'}
            preloadEngines
          />
        )}
        subtitle={waitingForTask ? '预送货任务二维码' : isCompleted ? '本单标签已全部确认' : `剩余 ${remaining} 张待确认`}
        title={processing ? '正在读取' : waitingForTask ? '请扫描任务码' : isCompleted ? '贴标完成' : '继续扫描标签'}
      />

      <div
        aria-live="polite"
        className={`label-scan-feedback label-scan-feedback--${scanFeedback.tone}`}
        data-testid="label-scan-feedback"
        role="status"
      >
        {scanFeedback.text}
      </div>

      {task ? (
        <section className="label-flow-facts" aria-label="贴标任务进度">
          <div className="label-flow-facts__head">
            <div>
              <span>预送货单</span>
              <strong>{task.note?.note_no || task.task_code}</strong>
              <small>任务码 {task.task_code}</small>
              {task.note?.total_qty != null ? <small>送货总件数 {task.note.total_qty}</small> : null}
            </div>
            <em>{isCompleted ? '已完成' : '贴标中'}</em>
          </div>
          <div className="label-flow-metrics">
            {[
              ['应贴', expected],
              ['打印', printStatus],
              ['已贴', attached],
              ['剩余', remaining],
            ].map(([label, value]) => (
              <div key={label}><strong>{value}</strong><span>{label}</span></div>
            ))}
          </div>
          {task.blocking_reasons?.length ? (
            <div className="label-flow-blocker">{task.blocking_reasons[0]}</div>
          ) : null}
        </section>
      ) : null}
    </PdaPage>
  );
}

export default LabelVerification;
